import React, { useMemo } from 'react';
import type { Control } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { pipe, Option, Array as A } from 'effect';

import AutocompleteVirtualizedBase from 'components/mui/Form/Autocomplete/AutocompleteVirtualizedBase';
import type { BeneficiaryFieldOption } from 'domain/payments/schemas';

import { getParentFieldName, getScopedOptions } from '../utils';
import type { BeneficiaryField, FormValues } from '../types';

// DPM-713: fields allowed to accept a value that is not in their option list.
// The Israeli branch registry lags real branch openings, so a user whose branch
// is missing must still be able to enter it. The form contract carries NO marker
// for this, so the field is selected by name — a deliberate, scoped exception to
// "read the contract, never field names".
// TO REPLACE: when the contract gains a per-field marker, read that instead.
const FREE_TEXT_FIELD_NAMES: readonly string[] = ['branch_code'];

// Per-field copy for "your search matched nothing". Keyed by field name because
// the message names the entity ("No banks matching…"), which the contract does
// not describe. Anything absent here falls back to the shared generic text.
const NO_MATCH_MESSAGE: Record<string, { id: string; defaultMessage: string }> = {
  bank_code: {
    id: 'beneficiary.modal.bank.noMatches',
    defaultMessage: 'No banks matching your search',
  },
};

interface AutocompleteFieldProps {
  field: BeneficiaryField;
  control: Control<FormValues>;
  // Current form values, already watched by the modal. Passed in rather than
  // watched here so this component holds no subscription of its own.
  values: FormValues;
  disabled: boolean;
  hasError: boolean;
  helperText?: React.ReactNode;
  // Called when this field commits a new value, so the form can clear any field
  // scoped to it. Synchronous with the commit — a watch-based effect races the
  // re-render that re-resolves the child against its new option list.
  onValueCommitted?: (fieldName: string) => void;
}

const AutocompleteField: React.FC<AutocompleteFieldProps> = ({
  field,
  control,
  values,
  disabled,
  hasError,
  helperText,
  onValueCommitted,
}) => {
  const { formatMessage } = useIntl();

  const parentName = getParentFieldName(field);
  // `FormValues` permits `string[]` (multi-select). Narrow at runtime rather than
  // casting: an array is truthy, so a `dependsOn` pointing at a multi-select would
  // read as "parent chosen", picking the wrong empty state and a junk remount key.
  const parentValue = parentName
    ? pipe(
        Option.fromNullable(values[parentName]),
        Option.filter((value): value is string => typeof value === 'string'),
        Option.getOrElse(() => ''),
      )
    : undefined;

  // Load-bearing, not an optimisation: a fresh array identity every render makes
  // the virtualized listbox rebuild.
  const options = useMemo(
    () => getScopedOptions(field, parentName ? { [parentName]: parentValue ?? '' } : {}),
    [field, parentName, parentValue],
  );

  const isFreeText = FREE_TEXT_FIELD_NAMES.includes(field.name);

  // Three distinct empty states, which all otherwise read as the shared generic
  // "No options": no parent chosen yet, parent chosen but genuinely childless,
  // and (for an unscoped field) a search that matched nothing.
  const noOptionsText = useMemo(() => {
    if (!parentName) {
      // Never return undefined: `AutocompleteVirtualizedBase` spreads caller props
      // AFTER its own localised `noOptionsText`, so an undefined here would wipe it
      // and fall through to MUI's hardcoded English default.
      return pipe(
        Option.fromNullable(NO_MATCH_MESSAGE[field.name]),
        Option.map((message) => formatMessage(message)),
        Option.getOrElse(() =>
          formatMessage({ id: 'options.noOptionsText', defaultMessage: 'No options' }),
        ),
      );
    }
    return parentValue
      ? formatMessage({
          id: 'beneficiary.modal.branch.noBranches',
          defaultMessage: 'No branches found for this bank',
        })
      : formatMessage({
          id: 'beneficiary.modal.branch.selectBankFirst',
          defaultMessage: 'Select a bank first',
        });
  }, [parentName, parentValue, field.name, formatMessage]);

  return (
    <Controller
      name={field.name}
      control={control}
      render={({ field: f }) => {
        const current = typeof f.value === 'string' ? f.value : '';

        // Resolve against the SCOPED list only. Branch codes repeat across banks
        // ("001" exists under several), so resolving against the unscoped list
        // would display another bank's branch label.
        const value: BeneficiaryFieldOption | null = pipe(
          options,
          A.findFirst((option) => option.value === current),
          // A free-text value matches no option; surface it as its own entry so
          // the input still shows what the user typed.
          Option.orElse(() =>
            isFreeText && current ? Option.some({ label: current, value: current }) : Option.none(),
          ),
          Option.getOrNull,
        );

        // Store the option's `value`, never its label or the raw typed string, so
        // a branch picked from the list and the same branch typed by hand are
        // recorded identically.
        const commit = (raw: string) =>
          pipe(
            options,
            A.findFirst((option) => option.value === raw || option.label === raw),
            Option.map((option) => option.value),
            Option.getOrElse(() => raw),
            (resolved) => {
              f.onChange(resolved);
              if (resolved !== current) onValueCommitted?.(field.name);
            },
          );

        return (
          <AutocompleteVirtualizedBase
            // Remount when the parent's value changes. MUI keeps its internal
            // input text when `value` is cleared programmatically, so without
            // this the branch box still displays the previous bank's branch
            // while the form value is empty — the user sees a filled field and
            // a disabled submit with no explanation.
            key={parentName ? `${field.name}:${parentValue ?? ''}` : field.name}
            fullWidth
            options={options}
            value={value}
            freeSolo={isFreeText}
            disabled={disabled}
            noOptionsText={noOptionsText}
            getOptionLabel={(option: BeneficiaryFieldOption | string) =>
              typeof option === 'string' ? option : option.label
            }
            onChange={(_: unknown, option: BeneficiaryFieldOption | string | null) => {
              if (typeof option === 'string') {
                commit(option);
                return;
              }
              const next = option?.value ?? '';
              f.onChange(next);
              if (next !== current) onValueCommitted?.(field.name);
            }}
            // freeSolo only: commit raw typing so an unlisted branch survives
            // without requiring Enter. Non-free-text fields must NOT do this, or
            // they would accept values they never offered.
            onInputChange={
              isFreeText
                ? (_: unknown, inputValue: string, reason: string) =>
                    reason === 'input' ? commit(inputValue) : undefined
                : undefined
            }
            textFieldProps={{
              label: field.label,
              required: true,
              size: 'small',
              error: hasError,
              helperText,
              // Feed Controller's blur back to react-hook-form. Without it the
              // field is never marked touched, and since the form validates on
              // `onTouched` an empty required autocomplete would never show its
              // error — unlike the plain text fields, which get onBlur from
              // `register`.
              onBlur: f.onBlur,
            }}
          />
        );
      }}
    />
  );
};

export default AutocompleteField;
